import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getExtraWorkList } from "../../services/extraWorkService";
import { formatDate } from "../../utils/formatDate";

const ExtraWorkList = () => {
  const navigate = useNavigate();
  const [extraWorkList, setExtraWorkList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchExtraWork() {
      try {
        const data = await getExtraWorkList();
        setExtraWorkList(data);
      } catch {
        setError("Failed to load extra work");
      } finally {
        setLoading(false);
      }
    }
    fetchExtraWork();
  }, []);

  const getProjectRoute = (work) =>
    work.project_id ? `/projects/${work.project_id}` : "/projects";

  if (loading) return <div className="p-6 text-center">Loading...</div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto pb-24">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Extra Work</h1>
        <Link
          to="/projects"
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          View Projects
        </Link>
      </div>

      {error && <p className="text-red-500 text-center">{error}</p>}
      <p className="text-sm text-gray-500 mb-4">
        Extra work items are managed from their related project pages.
      </p>

      {/* ===== MOBILE CARDS ===== */}
      <div className="space-y-4 md:hidden">
        {extraWorkList.length === 0 ? (
          <p className="text-center text-gray-500">No extra work found.</p>
        ) : (
          extraWorkList.map((work) => (
            <div
              key={work.id}
              className="bg-white rounded-xl shadow p-4 border"
            >
              <div className="flex justify-between items-start">
                <h3 className="font-semibold">{work.title}</h3>
                <button
                  onClick={() => navigate(getProjectRoute(work))}
                  className="text-blue-600 text-sm"
                >
                  Open Project
                </button>
              </div>

              <p className="text-sm text-gray-600 mt-1">
                Project: <strong>{work.project_name}</strong>
              </p>

              <p className="text-sm text-gray-600 mt-1">
                Date: {formatDate(work.date_created)}
              </p>

              <p className="text-sm mt-2">
                Status:{" "}
                <span className="font-semibold capitalize">
                  {work.status}
                </span>
              </p>
            </div>
          ))
        )}
      </div>

      {/* ===== DESKTOP TABLE ===== */}
      <div className="hidden md:block bg-white p-6 rounded shadow overflow-x-auto">
        {extraWorkList.length === 0 ? (
          <p className="text-center text-gray-500">No extra work found.</p>
        ) : (
          <table className="min-w-full table-auto text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-left">Project</th>
                <th className="px-4 py-2 text-left">Date Created</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {extraWorkList.map((work) => (
                <tr key={work.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2">{work.title}</td>
                  <td className="px-4 py-2">{work.project_name}</td>
                  <td className="px-4 py-2">
                    {formatDate(work.date_created)}
                  </td>
                  <td className="px-4 py-2 capitalize">{work.status}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => navigate(getProjectRoute(work))}
                      className="text-blue-600 hover:underline"
                    >
                      Open Project
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ExtraWorkList;
